/**
 * Human-readable labels and notes for governance_events rows.
 *
 * Catchball RPCs persist machine payloads in `note` (json_build_object::text).
 * Cycle Results and the Accountability Trail used to dump that JSON — UUIDs
 * and all — into the UI. Format here; never show a raw payload to a person.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const EVENT_TYPE_LABELS: Record<string, string> = {
  catchball_ball_passed_down: 'Ball passed down',
  catchball_ball_passed_up: 'Ball passed up',
  proposal_submitted: 'Proposal sent up',
  proposal_accepted: 'Proposal accepted',
  proposal_accepted_provisional: 'Proposal accepted with conditions',
  loop_accepted: 'Loop accepted',
  loop_created: 'Loop opened',
  loop_opened: 'Loop opened',
  loop_closed: 'Loop closed',
  request_issued: 'Request sent',
  track_result_published: 'Result published for the track',
  assignment_released_in_part: 'Part of an assignment released',
  commitment_merged: 'Commitments merged into one',
};

export type OrgUnitNameResolver = (orgUnitId: string) => string | undefined;

function isUuid(value: unknown): boolean {
  return typeof value === 'string' && UUID_RE.test(value);
}

function looksLikeJsonObject(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.startsWith('{') && trimmed.endsWith('}');
}

function parseNoteObject(note: string): Record<string, unknown> | null {
  if (!looksLikeJsonObject(note)) return null;
  try {
    const parsed = JSON.parse(note) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function titleCaseSnake(value: string): string {
  return value
    .replace(/^catchball_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Headline for a governance_events.event_type (DB snake_case or canon UPPER). */
export function formatGovernanceEventType(eventType: string): string {
  const key = eventType.trim().toLowerCase();
  if (EVENT_TYPE_LABELS[key]) return EVENT_TYPE_LABELS[key];
  return titleCaseSnake(key);
}

function resolveName(
  id: unknown,
  resolveOrgUnitName?: OrgUnitNameResolver,
): string | undefined {
  if (typeof id !== 'string' || !id) return undefined;
  const named = resolveOrgUnitName?.(id)?.trim();
  if (named) return named;
  return undefined;
}

function formatPassPayload(
  payload: Record<string, unknown>,
  resolveOrgUnitName?: OrgUnitNameResolver,
): string | null {
  const fromName = resolveName(payload.from_org_unit_id, resolveOrgUnitName);
  const toName = resolveName(payload.to_org_unit_id, resolveOrgUnitName);
  if (fromName && toName) return `From ${fromName} to ${toName}`;
  if (toName) return `To ${toName}`;
  if (fromName) return `From ${fromName}`;
  const direction = payload.pass_direction === 'up' ? 'up' : payload.pass_direction === 'down' ? 'down' : null;
  return direction ? `Passed ${direction}` : null;
}

function formatLoopAcceptedPayload(payload: Record<string, unknown>): string | null {
  const parts: string[] = [];
  const loopNumber = payload.loopNumber ?? payload.loop_number;
  if (typeof loopNumber === 'number') parts.push(`Loop ${loopNumber}`);
  const sessionCount = payload.sessionCount ?? payload.session_count;
  if (typeof sessionCount === 'number') {
    parts.push(`${sessionCount} session${sessionCount === 1 ? '' : 's'}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function firstHumanString(payload: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim() && !isUuid(value) && !looksLikeJsonObject(value)) {
      return value.trim();
    }
  }
  return null;
}

/**
 * Body copy for governance_events.note. Returns null when the note is empty
 * or only machine identifiers — callers should omit the line rather than
 * fall back to the raw string.
 */
export function formatGovernanceEventNote(
  note: string | null | undefined,
  options?: { eventType?: string; resolveOrgUnitName?: OrgUnitNameResolver },
): string | null {
  if (!note || !note.trim()) return null;

  const payload = parseNoteObject(note);
  if (!payload) {
    if (looksLikeJsonObject(note) || isUuid(note.trim())) return null;
    return note.trim();
  }

  const eventType = (options?.eventType ?? '').toLowerCase();
  const resolve = options?.resolveOrgUnitName;

  if (
    eventType.includes('ball_passed') ||
    'from_org_unit_id' in payload ||
    'to_org_unit_id' in payload
  ) {
    const pass = formatPassPayload(payload, resolve);
    if (pass) return pass;
  }

  if (eventType === 'loop_accepted' || 'loopNumber' in payload || 'loop_number' in payload) {
    const loop = formatLoopAcceptedPayload(payload);
    if (loop) return loop;
  }

  const prose = firstHumanString(payload, ['note', 'message', 'rationale', 'summary']);
  if (prose) return prose;

  return null;
}
